import { clsx } from 'clsx';
import type { ReactNode } from 'react';

export interface DataCardProps {
  title: ReactNode;
  subtitle?: string;
  badge?: ReactNode;
  headerRight?: ReactNode;
  children: ReactNode;
  accent?: 'default' | 'primary' | 'success' | 'warn' | 'danger';
  glass?: boolean;
  onClick?: () => void;
  className?: string;
  id?: string;
}

const ACCENT_BORDER: Record<string, string> = {
  default: 'var(--primary)', primary: 'var(--primary)', success: 'var(--success)', warn: 'var(--warn)', danger: 'var(--danger)',
};

export function DataCard({ title, subtitle, badge, headerRight, children, accent, glass = false, onClick, className, id }: DataCardProps) {
  const Tag = onClick ? 'button' : 'div';
  return (
    <Tag
      id={id}
      className={clsx('relative overflow-hidden rounded-xl border text-left transition-all duration-200 animate-fade-in scroll-mt-20 card-highlight shadow-xs', glass ? 'border-border backdrop-blur-md' : 'border-border bg-surface-1', 'hover:-translate-y-0.5 hover:shadow-lg hover:border-border-strong', onClick && 'cursor-pointer active:scale-[0.98]', className)}
      style={glass ? { backgroundColor: 'color-mix(in srgb, var(--surface-1) 80%, transparent)' } : undefined}
      onClick={onClick}
    >
      {accent && <div className="absolute inset-x-0 top-0 h-[3px]" style={{ background: ACCENT_BORDER[accent] ?? ACCENT_BORDER.default }} />}
      <div className={clsx('flex items-start justify-between gap-3 px-5 pt-4 pb-3', accent && 'pt-5')}>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h3 className="truncate text-sm font-semibold text-ink">{title}</h3>
            {badge}
          </div>
          {subtitle && <p className="mt-0.5 text-2xs text-ink-muted">{subtitle}</p>}
        </div>
        {headerRight && <div className="shrink-0">{headerRight}</div>}
      </div>
      <div className="px-5 pb-4">{children}</div>
    </Tag>
  );
}
